import {ApplicationData, Options, Question, QuestionOptions} from '@agenium-ai/agenium-client-sdk/dist/models';
import {Section} from '@agenium-ai/agenium-client-sdk/dist/models/section';
import {Button, CircularProgress, Dialog, DialogContent, Grid, makeStyles} from '@material-ui/core';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import {observer} from 'mobx-react-lite';
import React, {createRef, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {firstValueFrom} from 'rxjs';
import {useAgenium} from '../../Core/agenium/ageniumContext';
import {useAgeniumUser} from '../../Core/hooks/useAgeniumUser';
import {ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import RootStore from '../../Core/mobx/RootStore';
import LoadingSpinner from '../../UI/LoadingSpinner';
import Address from './InstantAppModalPages/Address';
import AgeniumApplication from './InstantAppModalPages/AgeniumApplication/AgeniumApplication';
import {AllDone} from './InstantAppModalPages/AllDone';
import ApprovalInformation from './InstantAppModalPages/ApprovalInformation';
import BankingInformation from './InstantAppModalPages/BankingInformation';
import Beneficiaries from './InstantAppModalPages/Beneficiaries';
import Birthplace from './InstantAppModalPages/Birthplace';
import BookAppointment from './InstantAppModalPages/BookAppointment';
import ConditionalCoverage from './InstantAppModalPages/ConditionalCoverage';
import ConditionalCoverageConfirm from './InstantAppModalPages/ConditionalCoverageConfirm';
import DecisionApproval from './InstantAppModalPages/DecisionApproval';
import DecisionDeclined from './InstantAppModalPages/DecisionDeclined';
import DecisionHardStop from './InstantAppModalPages/DecisionHardStop';
import DecisionManualUnderwriting from './InstantAppModalPages/DecisionManualUnderwriting';
import Employer from './InstantAppModalPages/Employer';
import ExistingLifeInsurance from './InstantAppModalPages/ExistingLifeInsurance';
import LastDoctorVisit from './InstantAppModalPages/LastDoctorVisit';
import Review from './InstantAppModalPages/ReviewScreen';
import {SendDocusign} from './InstantAppModalPages/SendDocusign';
import {StartApplication} from './InstantAppModalPages/StartApplication';
import UnderwritingDisclosure from './InstantAppModalPages/UnderwritingDisclosure';
import {VerifyIdentity} from './InstantAppModalPages/VerifyIdentity';
import {getApprovalQuoteFromCalcfocus} from './InstantAppModalPages/utils';
import {updateHubspotProsperDealDocusignComplete} from './deal';
import AgentAssistPanel from '../Assist/AgentAssistPanel';

export type InstantAppModalPage =
    | 'START_APPLICATION'
    | 'APPLICATION'
    | 'REVIEW'
    // | 'GENERAL_QUESTIONS'
    | 'VERIFY_IDENTITY'
    | 'BOOK_APPOINTMENT'
    | 'BANKING_INFO'
    | 'BANKING_INFO_LF'
    | 'APPROVAL_INFO'
    | 'APPROVAL_INFO_LF'
    | 'DECISION_APPROVAL'
    | 'DECISION_DECLINED'
    | 'DECISION_MANUAL_UNDERWRITING'
    | 'DECISION_HARD_STOP'
    | 'SEND_DOCUSIGN'
    | 'ALL_DONE'
    | 'BENEFICIARIES'
    | 'EMPLOYER'
    | 'EXISTING_LIFE_INSURANCE'
    | 'LAST_DOCTOR_VISIT'
    | 'BIRTHPLACE'
    | 'ADDRESS'
    | 'CONDITIONAL_COVERAGE'
    | 'CONDITIONAL_COVERAGE_CONFIRM'
    | 'UNDERWRITING_DISCLOSURE_POST';

interface FullScreenModalProps {
    onClose: () => void;
    parameterStore: ParameterStore;
    formType: 'single-quote' | 'combination-quote' | undefined;
    quoteResponse: Outlist[];
}
const useStyles = makeStyles((theme) => ({
    dialog: {
        '& #paperDialogContainer': {
            height: '100%',
        },
        '& #columnLeftSidebar': {
            background: '#f4f6ff',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            '& img': {
                width: 'calc(100% - 28%)',
                maxWidth: '171px',
                margin: '34px 14%',
            },
            '& button': {
                margin: '34px 14%',
                marginTop: 'auto',
                borderRadius: '20px',
            },
        },
        '& #columnContent': {
            background: '#fff',
            width: '100%',
            height: 'calc(100% - 100px)',
            padding: '10px 20px',
            overflowY: 'auto',
        },
        '& #columnToolbarSidebar': {
            background: '#fff',
            paddingLeft: '20px',
            paddingRight: '20px',
            height: 'calc(100% - 100px)',
            overflowY: 'auto',
            display: 'flex',
            justifyContent: 'center',
        },
        '& .MuiDialog-paper': {
            margin: 0,
            width: '100vw',
            height: '100vh',
        },
        '& .MuiDialogContent-root': {
            margin: 0,
            padding: 0,
            width: '100%',
            height: '100%',
        },
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
}));

export type CalcFocusData = {
    faceAmount?: number;
    monthlyPremium?: number;
    productName?: string;
    lapseAge?: number;
    quoteAge?: number;
    out?: Outlist[];
    req?: any;
} | null;

export type Outlist = {
    name: string;
    value: string | number | number[];
    type: string;
    fieldName?: string;
};

const InstantAppModal: React.FC<FullScreenModalProps> = observer(
    ({onClose, parameterStore, formType, quoteResponse}) => {
        const ageniumContext = useAgenium();
        const [calcFocusDataLocal, setCalcFocusDataLocal] = useState<CalcFocusData>(null);
        const {prequal} = parameterStore || {};
        const dialogContentRef = createRef();
        const location = useLocation();
        const history = useHistory();
        const searchParams = new URLSearchParams(location.search);
        const modalPage = searchParams.get('modalPage')?.toUpperCase() as InstantAppModalPage | null;
        const classes = useStyles();
        const {ageniumUser} = useAgeniumUser();
        const contactFormStore = RootStore.agentPortalStore.contactsFormStore;
        const [assistPanelExpanded, setAssistPanelExpanded] = useState(true);
        const [isFormSaving, setIsFormSaving] = useState(false);
        const [prevModal, setPrevModal] = useState<string | null | undefined>();
        async function setModalPage(modalPage: InstantAppModalPage | null) {
            if (modalPage) {
                setPrevModal(searchParams.get('modalPage')?.toUpperCase() ?? null);
                searchParams.set('modalPage', modalPage);
                history.push({
                    search: searchParams.toString(),
                });
            } else {
                setIsFormSaving(true);
                await RootStore.agentPortalStore.contactsFormStore.saveForm();
                setPrevModal(null);
                setIsFormSaving(false);
                onClose();
            }
        }

        const onError = (error: any) => {
            RootStore.uiStore.showSnackbar(error?.message, 'error');
        };

        useEffect(() => {
            if (
                (modalPage === 'DECISION_APPROVAL' ||
                    modalPage === 'BOOK_APPOINTMENT' ||
                    modalPage === 'BANKING_INFO_LF' ||
                    modalPage === 'BANKING_INFO') &&
                parameterStore.approval.decisionLater1HrMark.value
            ) {
                setModalPage('APPROVAL_INFO_LF');
            }
        }, []);

        // Lock the tab to 'prosper' synchronously (useLayoutEffect fires before any useEffect),
        // preventing the quotes page's AgentAssistPanel from overriding it.
        useLayoutEffect(() => {
            RootStore.scriptAssistStore.lockTab('prosper');
            return () => {
                RootStore.scriptAssistStore.unlockTab();
                RootStore.scriptAssistStore.setApplicationData(null);
                RootStore.scriptAssistStore.setCurrentQuestion(null);
                RootStore.scriptAssistStore.setModalPage(null);
                RootStore.scriptAssistStore.setCalcFocusData(null);
            };
        }, []);

        // Keep scriptAssistStore in sync with Agenium state for trigger evaluation.
        useEffect(() => {
            RootStore.scriptAssistStore.setApplicationData(ageniumContext.applicationData ?? null);
        }, [ageniumContext.applicationData]);

        useEffect(() => {
            RootStore.scriptAssistStore.setCurrentQuestion(ageniumContext.question ?? null);
        }, [ageniumContext.question]);

        useEffect(() => {
            RootStore.scriptAssistStore.setModalPage(modalPage ?? null);
        }, [modalPage]);

        useEffect(() => {
            RootStore.scriptAssistStore.setCalcFocusData(calcFocusDataLocal ?? null);
        }, [calcFocusDataLocal]);

        const applicationContent = useMemo(() => {
            if (ageniumContext.ageniumUserLoading) {
                return <LoadingSpinner />;
            }
            if (!ageniumContext.hasValidAgent) {
                return (
                    <div>
                        You are not appointed with EMC. Have your manager add your Agenium appointment info, and try
                        logging out and logging back in.
                    </div>
                );
            }
            return (
                <>
                    {modalPage === 'START_APPLICATION' && (
                        <StartApplication
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            formType={formType}
                            quoteResponse={quoteResponse}
                            onExit={() => {}}
                            onNext={() => setModalPage('REVIEW')}
                        />
                    )}
                    {modalPage === 'APPLICATION' && (
                        <AgeniumApplication
                            quoteResponse={quoteResponse}
                            dialogContentRef={dialogContentRef}
                            updateLocalCalcfocusData={setCalcFocusDataLocal}
                            ageniumContext={ageniumContext}
                            onBack={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'REVIEW' && (
                        <Review
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            formType={formType}
                            quoteResponse={quoteResponse}
                            onNext={() => setModalPage('BENEFICIARIES')}
                            onExit={() => {}}
                        />
                    )}
                    {/*{modalPage === 'GENERAL_QUESTIONS' && (
                        <GeneralQuestions
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('BENEFICIARIES')}
                            onExit={() => {}}
                        />
                    )}*/}
                    {modalPage === 'VERIFY_IDENTITY' && (
                        <VerifyIdentity
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            quoteResponse={quoteResponse}
                            onNext={() => setModalPage('APPLICATION')}
                            onExit={() => {}}
                        />
                    )}
                    {modalPage === 'BENEFICIARIES' && (
                        <Beneficiaries
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('EMPLOYER')}
                            onBack={() => setModalPage('REVIEW')}
                            onExit={() => {}}
                        />
                    )}
                    {modalPage === 'LAST_DOCTOR_VISIT' && (
                        <LastDoctorVisit
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onBack={() => setModalPage('EXISTING_LIFE_INSURANCE')}
                            onNext={() => setModalPage('BIRTHPLACE')}
                            onExit={() => {}}
                        />
                    )}
                    {modalPage === 'ADDRESS' && (
                        <Address
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('VERIFY_IDENTITY')}
                            onBack={() => setModalPage('BIRTHPLACE')}
                            onExit={() => {}}
                        />
                    )}
                    {modalPage === 'BIRTHPLACE' && (
                        <Birthplace
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('ADDRESS')}
                            onBack={() => setModalPage('LAST_DOCTOR_VISIT')}
                            onExit={() => {}}
                        />
                    )}
                    {modalPage === 'EMPLOYER' && (
                        <Employer
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('EXISTING_LIFE_INSURANCE')}
                            onBack={() => setModalPage('BENEFICIARIES')}
                            onExit={() => {}}
                        />
                    )}
                    {modalPage === 'EXISTING_LIFE_INSURANCE' && (
                        <ExistingLifeInsurance
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onNext={() => setModalPage('LAST_DOCTOR_VISIT')}
                            onBack={() => setModalPage('EMPLOYER')}
                            onExit={() => {}}
                        />
                    )}
                    {modalPage === 'DECISION_APPROVAL' && (
                        <DecisionApproval
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            onShareDecisionNow={function (): void {
                                setModalPage('APPROVAL_INFO');
                            }}
                            onBookAppointment={function (): void {
                                setModalPage('BOOK_APPOINTMENT');
                            }}
                            onExit={() => {}}
                        />
                    )}
                    {modalPage === 'DECISION_DECLINED' && (
                        <DecisionDeclined
                            onExit={() => {}}
                            onNext={function (): void {
                                searchParams.delete('modalPage');
                                history.push({
                                    pathname: `/agent/form/${contactFormStore.email}/${contactFormStore.id}/1`,
                                });
                            }}
                            parameterStore={parameterStore}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'DECISION_MANUAL_UNDERWRITING' && (
                        <DecisionManualUnderwriting
                            onExit={() => {}}
                            onNext={function (): void {
                                setModalPage('CONDITIONAL_COVERAGE');
                            }}
                            onBackToClients={() => {
                                searchParams.delete('modalPage');
                                history.push({
                                    pathname: '/agent',
                                });
                            }}
                            contactFormStore={contactFormStore}
                            parameterStore={parameterStore}
                            prevModal={prevModal}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'DECISION_HARD_STOP' && (
                        <DecisionHardStop
                            onExit={() => {}}
                            onNext={function (): void {
                                history.push({
                                    pathname: '/agent',
                                });
                            }}
                            parameterStore={parameterStore}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'BOOK_APPOINTMENT' && (
                        <BookAppointment onNext={() => setModalPage('APPROVAL_INFO')} onExit={() => {}} />
                    )}
                    {(modalPage === 'BANKING_INFO' || modalPage === 'BANKING_INFO_LF') && (
                        <BankingInformation
                            parameterStore={parameterStore}
                            onNext={async () => {
                                // save form because some of this info
                                // is in the parameter store
                                await contactFormStore.saveForm();
                                setModalPage('UNDERWRITING_DISCLOSURE_POST');
                            }}
                            onBack={() => setModalPage('APPROVAL_INFO')}
                            onExit={() => {}}
                            calcFocusDataLocal={calcFocusDataLocal}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'UNDERWRITING_DISCLOSURE_POST' && (
                        <UnderwritingDisclosure
                            parameterStore={parameterStore}
                            onNext={() => setModalPage('SEND_DOCUSIGN')}
                            onBack={() => setModalPage('BANKING_INFO')}
                            onExit={() => {}}
                            ageniumContext={ageniumContext}
                            isPostDecision={true}
                        />
                    )}
                    {modalPage === 'APPROVAL_INFO' && (
                        <ApprovalInformation
                            dialogContentRef={dialogContentRef}
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            calcFocusDataLocal={calcFocusDataLocal}
                            setCalcFocusDataLocal={setCalcFocusDataLocal}
                            onNext={async () =>
                                new Promise<void>((resolve, _reject) => {
                                    resolve();
                                    setModalPage('BANKING_INFO');
                                })
                            }
                            onExit={() => {}}
                            // onBack={() => setModalPage('DECISION_APPROVAL')}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'APPROVAL_INFO_LF' && (
                        <ApprovalInformation
                            dialogContentRef={dialogContentRef}
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            calcFocusDataLocal={calcFocusDataLocal}
                            setCalcFocusDataLocal={setCalcFocusDataLocal}
                            {...(prevModal === 'BANKING_INFO_LF'
                                ? {
                                      onBack: () => setModalPage('BANKING_INFO_LF'),
                                  }
                                : {})}
                            onBackToClients={() => {
                                searchParams.delete('modalPage');
                                history.push({
                                    pathname: '/agent',
                                });
                            }}
                            onNext={async () =>
                                new Promise<void>((resolve, reject) => {
                                    try {
                                        if (
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Bank_RoutingNumber',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Bank_AccountNumber',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Bank_AccountType',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_LapseNotice',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_PaymentMode',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_PaymentModeEFTInitialDraft',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_PaymentFrequency',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_PaymentFrequencyOther',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_RequestedEffectiveDate',
                                            )?.value ||
                                            ageniumContext?.applicationData?.find(
                                                (i) => i.key === 'Case_Applicant_FaceAmountFinal',
                                            )?.value
                                        ) {
                                            ageniumContext?.sdk?.getSections().subscribe({
                                                next: async (section: Section[] | undefined) => {
                                                    let case_Applicant_FaceAmountFinal: Question | undefined =
                                                        undefined;
                                                    for (let t = 0; section && t < section.length; t++) {
                                                        for (
                                                            let i = 0;
                                                            section[t].questions && i < section[t].questions.length;
                                                            i++
                                                        ) {
                                                            if (
                                                                section[t].questions[i].key ===
                                                                'Case_Applicant_FaceAmountFinal'
                                                            )
                                                                case_Applicant_FaceAmountFinal =
                                                                    section[t].questions[i];
                                                            section[t].questions[i].questions?.forEach((l) => {
                                                                if (l.key === 'Case_Applicant_FaceAmountFinal')
                                                                    case_Applicant_FaceAmountFinal = l;
                                                            });
                                                        }
                                                    }
                                                    if (case_Applicant_FaceAmountFinal) {
                                                        if (
                                                            calcFocusDataLocal &&
                                                            calcFocusDataLocal.out &&
                                                            calcFocusDataLocal.out.length
                                                        )
                                                            case_Applicant_FaceAmountFinal.value =
                                                                calcFocusDataLocal.out.find(
                                                                    (t: Outlist) => t.fieldName === 'faceAmount',
                                                                )?.value;
                                                        else
                                                            case_Applicant_FaceAmountFinal.value = (
                                                                await getApprovalQuoteFromCalcfocus(
                                                                    ageniumContext.applicationData as ApplicationData[],
                                                                    ageniumUser,
                                                                )
                                                            )?.out?.find(
                                                                (t: Outlist) => t.fieldName === 'faceAmount',
                                                            )?.value;
                                                        try {
                                                            if (
                                                                ageniumContext.sdk &&
                                                                (await firstValueFrom(
                                                                    ageniumContext.sdk.submitAnswer(
                                                                        case_Applicant_FaceAmountFinal,
                                                                    ),
                                                                ))
                                                            ) {
                                                                let options: Options = {};
                                                                let questionOptions: QuestionOptions = {
                                                                    nextUnansweredQuestion: true,
                                                                };
                                                                options.questionOptions = questionOptions;
                                                                await firstValueFrom(
                                                                    ageniumContext.sdk.getNextQuestion(
                                                                        case_Applicant_FaceAmountFinal,
                                                                        options,
                                                                    ),
                                                                );
                                                                resolve();
                                                                setModalPage('SEND_DOCUSIGN');
                                                            }
                                                        } catch (e) {
                                                            onError(
                                                                'Error during submission of the Applicant Face Amount Final',
                                                            );
                                                            resolve();
                                                        }
                                                    }
                                                },
                                            });
                                        } else {
                                            resolve();
                                            setModalPage('BANKING_INFO');
                                        }
                                    } catch (e) {
                                        console.log(e);
                                        onError(e);
                                        resolve();
                                    }
                                })
                            }
                            onExit={() => {}}
                            ageniumContext={ageniumContext}
                        />
                    )}
                    {modalPage === 'SEND_DOCUSIGN' && (
                        <SendDocusign
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            quoteResponse={quoteResponse}
                            onDocusignSignComplete={async () => {
                                await updateHubspotProsperDealDocusignComplete();
                                setModalPage('ALL_DONE');
                            }}
                            onExit={() => {}}
                            onBack={() => setModalPage('BANKING_INFO')}
                            ageniumContext={ageniumContext}
                            onDecisionRedirect={(decision) => {
                                if (decision === 'REFERRED') {
                                    setModalPage('DECISION_MANUAL_UNDERWRITING');
                                } else if (decision === 'DECLINED' || decision === 'CANCELLED') {
                                    setModalPage('DECISION_DECLINED');
                                }
                            }}
                        />
                    )}
                    {modalPage === 'CONDITIONAL_COVERAGE' && (
                        <ConditionalCoverage
                            parameterStore={parameterStore}
                            contactFormStore={contactFormStore}
                            ageniumContext={ageniumContext}
                            onNext={() => setModalPage('CONDITIONAL_COVERAGE_CONFIRM')}
                        />
                    )}
                    {modalPage === 'CONDITIONAL_COVERAGE_CONFIRM' && (
                        <ConditionalCoverageConfirm
                            parameterStore={parameterStore}
                            quoteResponse={quoteResponse}
                            onExit={() => setModalPage(null)}
                        />
                    )}
                    {modalPage === 'ALL_DONE' && (
                        <AllDone
                            calcFocusDataLocal={calcFocusDataLocal}
                            onBackToClientsClicked={() => setModalPage(null)}
                            ageniumContext={ageniumContext}
                        />
                    )}
                </>
            );
        }, [
            modalPage,
            ageniumContext.ageniumUserLoading,
            ageniumContext.hasValidAgent,
            ageniumContext,
            parameterStore,
            contactFormStore,
            formType,
            quoteResponse,
            calcFocusDataLocal,
            prevModal,
        ]);

        return (
            <Dialog onClose={() => setModalPage(null)} fullScreen className={classes.dialog} open>
                <DialogContent ref={dialogContentRef}>
                    <Grid container id="paperDialogContainer">
                        <Grid item xs={2} id="columnLeftSidebar">
                            <img src="/img/logo3.svg" alt="Amplify Logo - Instant Application" />
                            {!ageniumContext.ageniumUserLoading && ageniumContext.hasValidAgent && (
                                <Button
                                    color="primary"
                                    variant="text"
                                    onClick={() => setModalPage(null)}
                                    startIcon={
                                        isFormSaving ? (
                                            <CircularProgress size={16} color="inherit" style={{marginRight: '10px'}} />
                                        ) : (
                                            <KeyboardBackspaceIcon />
                                        )
                                    }
                                    disabled={isFormSaving}
                                >
                                    Save and exit
                                </Button>
                            )}
                        </Grid>
                        <Grid item xs={assistPanelExpanded ? 7 : 9} id="columnContent">
                            {applicationContent}
                        </Grid>
                        <Grid item xs={assistPanelExpanded ? 3 : 1} id="columnToolbarSidebar">
                            <AgentAssistPanel
                                tab="prosper"
                                doOnExpand={() => setAssistPanelExpanded(true)}
                                doOnCollapse={() => setAssistPanelExpanded(false)}
                                onValueChangedNotes={(value) =>
                                    parameterStore?.onValueChangedPreQual({...prequal?.notes, name: 'notes'}, value)
                                }
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        );
    },
);

InstantAppModal.displayName = 'InstantAppModal';

export default InstantAppModal;
